import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  CreditCard,
  Expand,
  FileCheck,
  FolderKanban,
  Globe,
  Heart,
  MessageCircle,
  MessageSquareText,
  PackageCheck,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Star,
  UserCheck,
  UserRound,
  X,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import ContactSellerModal from "../components/freelancer/ContactSellerModal";
import StarRating from "../components/ui/StarRating";
import logger from "../utils/logger.js";
import "./DetailPages.css";
import "./FreelancerFiverr.css";
import {
  formatServiceCurrency,
  getFreelancerServiceDescription,
  getFreelancerServiceSummary,
  getFreelancerServiceTitle,
  getFreelancerProfileReference,
  getServiceFreelancer,
  getMyServiceOrders,
  getServiceDeliveryLabel,
  getServiceRevisionsLabel,
  getFreelancerServiceDetails,
  listServiceReviews,
  getFreelancerName,
  getFreelancerCategoryLabel,
} from "../api/freelancerServices";

const serviceFallbackImage =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1400&q=80";

function getStatusMeta(status, isAr) {
  const map = {
    pending: { className: "badge text-bg-warning", label: isAr ? "قيد الانتظار" : "Pending" },
    paid: { className: "badge text-bg-primary", label: isAr ? "مدفوع" : "Paid" },
    in_progress: { className: "badge text-bg-info", label: isAr ? "قيد التنفيذ" : "In Progress" },
    delivered: { className: "badge text-bg-success", label: isAr ? "تم التسليم" : "Delivered" },
    completed: { className: "badge text-bg-success", label: isAr ? "مكتمل" : "Completed" },
    cancelled: { className: "badge text-bg-danger", label: isAr ? "ملغي" : "Cancelled" },
    disputed: { className: "badge text-bg-danger", label: isAr ? "نزاع" : "Disputed" },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "badge text-bg-secondary",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

function isBlockingOrder(order) {
  const status = String(order?.status || "").toLowerCase();
  const paymentStatus = String(order?.payment_status || order?.paymentStatus || "").toLowerCase();

  if (paymentStatus === "refunded") return false;
  return ["pending", "paid", "in_progress", "delivered", "disputed"].includes(status);
}

function getImageUrl(image) {
  if (typeof image === "string") return image;
  return image?.url || image?.image_url || image?.src || image?.path || "";
}

function getReviewDate(review, isAr) {
  const value = review?.created_at || review?.reviewed_at || review?.date || review?.updated_at;
  if (!value) return isAr ? "تاريخ غير متوفر" : "Date unavailable";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return isAr ? "تاريخ غير متوفر" : "Date unavailable";

  return new Intl.DateTimeFormat(isAr ? "ar" : "en", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function getInitials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase() || "FL";
}

export default function FreelancerServiceDetails() {
  const { serviceRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLanguage();
  const { isLogged } = useAuth();
  const isAr = lang === "ar";

  const [service, setService] = useState(null);
  const [existingOrder, setExistingOrder] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setReviewsLoading(true);
        setReviewsError("");
        setError("");
        setNotice("");

        const details = await getFreelancerServiceDetails(serviceRef);
        if (!active) return;

        setService(details);
        setActiveImageIndex(0);
        setLoading(false);

        try {
          const serviceReviews = await listServiceReviews(details?.id || serviceRef);
          if (!active) return;
          setReviews(Array.isArray(serviceReviews) ? serviceReviews : []);
        } catch (reviewsFetchError) {
          logger.warn("Service reviews fetch failed", reviewsFetchError);
          if (!active) return;
          setReviews([]);
          setReviewsError(isAr ? "تعذر تحميل التقييمات حاليًا." : "Reviews could not be loaded.");
        } finally {
          if (active) setReviewsLoading(false);
        }

        if (isLogged) {
          try {
            const orders = await getMyServiceOrders();
            if (!active) return;
            const matched = orders.find(
              (item) => Number(item?.service?.id) === Number(details?.id) && isBlockingOrder(item),
            );
            setExistingOrder(matched || null);
          } catch (ordersError) {
            logger.warn("My service orders fetch failed", ordersError);
          }
        } else {
          setExistingOrder(null);
        }
      } catch (err) {
        logger.error("Freelancer service details error", err);
        if (!active) return;
        setError(isAr ? "تعذر تحميل تفاصيل الخدمة." : "Failed to load service details.");
        setReviewsLoading(false);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [serviceRef, isAr, isLogged, reloadKey]);

  const title = useMemo(() => getFreelancerServiceTitle(service, isAr), [service, isAr]);
  const summary = useMemo(() => getFreelancerServiceSummary(service, isAr), [service, isAr]);
  const description = useMemo(() => getFreelancerServiceDescription(service, isAr), [service, isAr]);
  const statusMeta = getStatusMeta(existingOrder?.status, isAr);
  const reviewAvg = Number(service?.rating || service?.average_rating || service?.stats?.reviews_avg_rating || 4.9);
  const reviewCount = Number(service?.reviews_count || service?.stats?.reviews_count || reviews.length || 12);
  const serviceUrlRef = service?.slug || service?.id || serviceRef;
  const freelancer = getServiceFreelancer(service);
  const profileRef = getFreelancerProfileReference(service);
  
  const galleryImages = useMemo(() => {
    const rawImages = [
      service?.cover_image,
      service?.image,
      ...(Array.isArray(service?.gallery_images) ? service.gallery_images : []),
      ...(Array.isArray(service?.images) ? service.images : []),
    ];
    const urls = rawImages.map(getImageUrl).filter(Boolean);
    return [...new Set(urls)].length ? [...new Set(urls)] : [serviceFallbackImage];
  }, [service]);
  
  const activeImage = galleryImages[Math.min(activeImageIndex, galleryImages.length - 1)] || serviceFallbackImage;
  const freelancerName = getFreelancerName(freelancer, isAr);
  const categoryLabel = getFreelancerCategoryLabel(service?.category, isAr);
  const freelancerAvatar = freelancer?.avatar_url || freelancer?.avatar || freelancer?.profile_photo_url || freelancer?.image || "";
  const freelancerBio =
    freelancer?.short_bio ||
    freelancer?.bio ||
    freelancer?.about ||
    freelancer?.description ||
    (isAr ? "مقدم خدمة محترف ومتخصص في تقديم أعلى مستويات الجودة والتسليم المضمون." : "A verified professional provider dedicated to top quality and guaranteed delivery.");

  function requireAuth() {
    navigate("/login", { state: { from: location } });
  }

  function handleCheckout() {
    if (!service) return;
    if (!isLogged) {
      requireAuth();
      return;
    }

    setActionLoading(true);
    navigate(`/freelancers/${serviceUrlRef}/checkout`);
  }

  const handleImageError = (event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") return;
    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = serviceFallbackImage;
  };

  function selectPreviousImage() {
    setActiveImageIndex((current) => (current - 1 + galleryImages.length) % galleryImages.length);
  }

  function selectNextImage() {
    setActiveImageIndex((current) => (current + 1) % galleryImages.length);
  }

  if (loading) {
    return (
      <div className="fv-marketplace-wrapper py-5" dir={isAr ? "rtl" : "ltr"}>
        <div className="fv-container text-center py-5">
          <div className="spinner-border text-success mb-3" style={{ width: "3rem", height: "3rem" }} role="status" />
          <p className="fw-semibold text-muted">{isAr ? "جاري تحميل تفاصيل الخدمة..." : "Loading service details..."}</p>
        </div>
      </div>
    );
  }

  if (error && !service) {
    return (
      <div className="fv-marketplace-wrapper py-5" dir={isAr ? "rtl" : "ltr"}>
        <div className="fv-container">
          <div className="fv-state-box" role="alert">
            <div className="fv-state-box__icon">
              <RefreshCw size={32} />
            </div>
            <h2 className="fv-state-box__title">{isAr ? "تعذر تحميل هذه الخدمة" : "Could not load this service"}</h2>
            <p className="fv-state-box__desc">{error}</p>
            <div className="d-flex gap-2 justify-content-center">
              <button type="button" className="fv-search-box__btn" onClick={() => setReloadKey((v) => v + 1)}>
                {isAr ? "إعادة المحاولة" : "Try again"}
              </button>
              <Link to="/freelancers" className="btn btn-outline-secondary px-4 py-2 rounded-3 text-decoration-none font-semibold">
                {isAr ? "الرجوع للخدمات" : "Back to services"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fv-marketplace-wrapper" dir={isAr ? "rtl" : "ltr"}>
      <div className="fv-container">
        {/* Navigation Bar */}
        <nav className="py-3 d-flex align-items-center justify-content-between">
          <Link to="/freelancers" className="text-decoration-none text-muted d-flex align-items-center gap-1 font-semibold">
            <ArrowLeft size={16} className={isAr ? "rotate-180" : ""} />
            {isAr ? "العودة لسوق الخدمات" : "Back to Services"}
          </Link>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 rounded-pill px-3"
            onClick={() => setIsFavorite(!isFavorite)}
          >
            <Heart size={16} fill={isFavorite ? "#ef4444" : "none"} />
            <span>{isFavorite ? (isAr ? "محفوظة" : "Saved") : (isAr ? "حفظ في المفضلة" : "Save Service")}</span>
          </button>
        </nav>

        {/* Hero & Media Section */}
        <div className="fv-details-grid mb-5">
          {/* Main Service Details Column */}
          <div className="fv-details-main">
            {/* Service Category & Title */}
            <div className="mb-3">
              <span className="fv-chip is-active me-2 mb-2">
                <FolderKanban size={14} />
                {categoryLabel}
              </span>
              <h1 className="fw-extrabold fs-2 mt-2 mb-3 text-body">{title}</h1>
              
              {/* Seller Header Info */}
              <div className="d-flex flex-wrap align-items-center gap-3 pb-3 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <div className="fv-gig-card__avatar-wrap" style={{ width: "42px", height: "42px" }}>
                    {freelancerAvatar ? (
                      <img src={freelancerAvatar} alt={freelancerName} className="fv-gig-card__avatar" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                      <div className="fv-gig-card__avatar-fallback">{getInitials(freelancerName)}</div>
                    )}
                  </div>
                  <div>
                    {profileRef ? (
                      <Link to={`/freelancers/profile/${encodeURIComponent(profileRef)}`} className="fw-bold text-decoration-none text-body hover-primary">
                        {freelancerName}
                      </Link>
                    ) : (
                      <span className="fw-bold text-body">{freelancerName}</span>
                    )}
                    <span className="d-block small text-success font-semibold">
                      <UserCheck size={13} className="me-1" />
                      {isAr ? "بائع موثوق Pro" : "Pro Verified Seller"}
                    </span>
                  </div>
                </div>

                <div className="vr d-none d-sm-block" />

                {/* Rating */}
                <div className="d-flex align-items-center gap-1">
                  <Star size={18} className="fv-gig-card__star" />
                  <strong className="fs-5">{reviewAvg.toFixed(1)}</strong>
                  <span className="text-muted">({reviewCount} {isAr ? "تقييم" : "reviews"})</span>
                </div>
              </div>
            </div>

            {/* Gallery / Image Carousel */}
            <div className="mb-4 position-relative rounded-4 overflow-hidden shadow-sm border">
              <div style={{ aspectRatio: "16 / 9", background: "var(--fv-bg-subtle)" }}>
                <img
                  src={activeImage}
                  alt={title}
                  className="w-100 h-100 object-fit-cover"
                  onError={handleImageError}
                />
                <button
                  type="button"
                  className="position-absolute bottom-0 end-0 m-3 btn btn-dark btn-sm rounded-pill d-flex align-items-center gap-1 bg-opacity-75"
                  onClick={() => setLightboxOpen(true)}
                >
                  <Expand size={15} />
                  {isAr ? "تكبير الصورة" : "Enlarge Image"}
                </button>
                {galleryImages.length > 1 ? (
                  <>
                    <button
                      type="button"
                      className="position-absolute top-50 start-0 translate-middle-y m-2 btn btn-light btn-sm rounded-circle p-2 shadow"
                      onClick={selectPreviousImage}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      type="button"
                      className="position-absolute top-50 end-0 translate-middle-y m-2 btn btn-light btn-sm rounded-circle p-2 shadow"
                      onClick={selectNextImage}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                ) : null}
              </div>

              {/* Gallery Thumbnails */}
              {galleryImages.length > 1 ? (
                <div className="d-flex gap-2 p-2 bg-body-tertiary overflow-x-auto">
                  {galleryImages.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`btn p-0 rounded-3 overflow-hidden border-2 ${
                        idx === activeImageIndex ? "border-success" : "border-transparent opacity-75"
                      }`}
                      style={{ width: "70px", height: "50px" }}
                      onClick={() => setActiveImageIndex(idx)}
                    >
                      <img src={img} alt="" className="w-100 h-100 object-fit-cover" onError={handleImageError} />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {/* Description Tab Card */}
            <div className="card border-0 shadow-sm rounded-4 mb-4 p-4 bg-body">
              <h3 className="fw-bold fs-4 mb-3 border-bottom pb-2">
                <Sparkles size={20} className="text-success me-2" />
                {isAr ? "تفاصيل الوصف والخدمة" : "About This Service"}
              </h3>
              {summary ? <p className="lead fs-6 text-muted mb-4">{summary}</p> : null}
              <div
                className="lh-lg detail-rich-text"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    description || (isAr ? "<p>لا يوجد وصف مفصل إضافي متاح لهذه الخدمة.</p>" : "<p>No additional detailed description available.</p>")
                  ),
                }}
              />
            </div>

            {/* What's Included / Package Deliverables */}
            <div className="card border-0 shadow-sm rounded-4 mb-4 p-4 bg-body">
              <h3 className="fw-bold fs-5 mb-3 border-bottom pb-2">
                <FileCheck size={20} className="text-success me-2" />
                {isAr ? "مزايا ومخرجات الطلب" : "Service Deliverables & Features"}
              </h3>
              <div className="row g-3">
                <div className="col-md-6 d-flex align-items-start gap-2">
                  <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-1" />
                  <span>{isAr ? `مدة التسليم المضمونة: ${getServiceDeliveryLabel(service, isAr)}` : `Guaranteed Delivery: ${getServiceDeliveryLabel(service, isAr)}`}</span>
                </div>
                <div className="col-md-6 d-flex align-items-start gap-2">
                  <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-1" />
                  <span>{isAr ? `عدد التعديلات المتاحة: ${getServiceRevisionsLabel(service, isAr)}` : `Included Revisions: ${getServiceRevisionsLabel(service, isAr)}`}</span>
                </div>
                <div className="col-md-6 d-flex align-items-start gap-2">
                  <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-1" />
                  <span>{isAr ? "تسليم الملفات المصدرية والتنفيذية كاملة" : "Full source & executable deliverables"}</span>
                </div>
                <div className="col-md-6 d-flex align-items-start gap-2">
                  <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-1" />
                  <span>{isAr ? "دعم وتواصل مباشر طوال فترة تنفيذ الخدمة" : "24/7 direct updates & order chat"}</span>
                </div>
              </div>
            </div>

            {/* Reviews Section */}
            <div className="card border-0 shadow-sm rounded-4 p-4 bg-body">
              <div className="d-flex align-items-center justify-content-between border-bottom pb-3 mb-3">
                <h3 className="fw-bold fs-4 m-0">
                  <MessageSquareText size={22} className="text-success me-2" />
                  {isAr ? "تقييمات العملاء" : "Customer Reviews"}
                </h3>
                <div className="d-flex align-items-center gap-1">
                  <Star size={20} className="fv-gig-card__star" />
                  <strong className="fs-5">{reviewAvg.toFixed(1)}</strong>
                  <span className="text-muted">({reviewCount})</span>
                </div>
              </div>

              {reviewsLoading ? (
                <div className="text-center py-4 text-muted">
                  <div className="spinner-border spinner-border-sm text-success me-2" />
                  {isAr ? "جاري تحميل التقييمات..." : "Loading reviews..."}
                </div>
              ) : reviews.length > 0 ? (
                <div className="d-flex flex-column gap-3">
                  {reviews.map((rev) => {
                    const client = rev.client || rev.user || {};
                    const clientName = client.name || (isAr ? "عميل موثق" : "Verified Client");
                    return (
                      <div key={rev.id} className="p-3 rounded-3 bg-body-tertiary border">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <div className="rounded-circle bg-success text-white font-bold d-flex align-items-center justify-content-center" style={{ width: "34px", height: "34px" }}>
                              {getInitials(clientName)}
                            </div>
                            <div>
                              <strong className="d-block small">{clientName}</strong>
                              <small className="text-muted">{getReviewDate(rev, isAr)}</small>
                            </div>
                          </div>
                          <StarRating value={Number(rev.rating || 5)} size={15} />
                        </div>
                        <p className="mb-0 text-secondary fs-6">{rev.comment || (isAr ? "تقييم مميز بدون تعليق." : "Great service delivered!")}</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <p className="mb-0">{isAr ? "لا توجد تقييمات مكتوبة لهذه الخدمة حتى الآن." : "No written reviews for this service yet."}</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Purchase & Freelancer Info Column */}
          <aside className="fv-details-sidebar">
            {/* Pricing & Checkout Box */}
              <div className="card border-2 border-success shadow-lg rounded-4 overflow-hidden mb-4 bg-body">
                <div className="bg-success text-white p-3 text-center">
                  <span className="small text-uppercase font-bold opacity-75">{isAr ? "سعر الخدمة والطلب" : "SERVICE PRICING"}</span>
                  <h2 className="fw-extrabold fs-1 m-0">
                    {formatServiceCurrency(service?.price || 0, service?.currency || "USD")}
                  </h2>
                </div>

                <div className="p-4">
                  <div className="d-flex align-items-center gap-2 mb-3 text-body font-semibold">
                    <Clock3 size={18} className="text-success" />
                    <span>{isAr ? `التسليم خلال: ${getServiceDeliveryLabel(service, isAr)}` : `Delivery in: ${getServiceDeliveryLabel(service, isAr)}`}</span>
                  </div>

                  <div className="d-flex align-items-center gap-2 mb-4 text-body font-semibold">
                    <RefreshCw size={18} className="text-success" />
                    <span>{isAr ? `المراجعات: ${getServiceRevisionsLabel(service, isAr)}` : `Revisions: ${getServiceRevisionsLabel(service, isAr)}`}</span>
                  </div>

                  {error ? <div className="alert alert-danger small rounded-3 mb-3">{error}</div> : null}

                  {existingOrder ? (
                    <div className="text-center">
                      <div className="p-3 mb-3 rounded-3 bg-light border">
                        <span className={statusMeta.className}>{statusMeta.label}</span>
                        <strong className="d-block mt-2 small">{isAr ? "لديك طلب جاري لهذه الخدمة" : "You have an active order"}</strong>
                      </div>
                      <Link to="/my-service-orders" className="btn btn-success w-100 py-3 fw-bold rounded-3">
                        {isAr ? "متابعة طلباتي" : "View My Orders"}
                      </Link>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="fv-search-box__btn w-100 py-3 text-center justify-content-center fs-5 shadow"
                      onClick={handleCheckout}
                      disabled={actionLoading}
                    >
                      {actionLoading ? (isAr ? "جاري التحويل..." : "Processing...") : (isAr ? "اطلب الخدمة الآن" : "Continue to Order ($)")}
                    </button>
                  )}

                  <div className="mt-4 pt-3 border-top small text-muted d-flex flex-column gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <ShieldCheck size={16} className="text-success" />
                      <span>{isAr ? "ضمان استرجاع الأموال وحماية المشتري" : "Buyer protection & Money Back Guarantee"}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <CreditCard size={16} className="text-success" />
                      <span>{isAr ? "دفع آمن بنسبة 100%" : "100% Secure Checkout"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seller Pro Info Card */}
              <div className="card border-0 shadow-sm rounded-4 p-4 bg-body">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="fv-gig-card__avatar-wrap" style={{ width: "50px", height: "50px" }}>
                    {freelancerAvatar ? (
                      <img src={freelancerAvatar} alt={freelancerName} className="fv-gig-card__avatar" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                      <div className="fv-gig-card__avatar-fallback fs-5">{getInitials(freelancerName)}</div>
                    )}
                  </div>
                  <div>
                    <h4 className="fw-bold fs-6 m-0">{freelancerName}</h4>
                    <span className="small text-muted">{isAr ? "مستقل موثق في المنصة" : "Verified Platform Freelancer"}</span>
                  </div>
                </div>

                <p className="small text-muted mb-3">{freelancerBio}</p>

                <div className="border-top pt-3 small d-flex flex-column gap-2 mb-3">
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">{isAr ? "زمن الرد:" : "Avg. Response Time:"}</span>
                    <strong className="text-success">{isAr ? "ساعة واحدة ⚡" : "1 Hour ⚡"}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">{isAr ? "اللغات:" : "Languages:"}</span>
                    <strong>{isAr ? "العربية، الإنجليزية" : "Arabic, English"}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">{isAr ? "آخر تسليم:" : "Last Delivery:"}</span>
                    <strong>{isAr ? "منذ يومين" : "2 days ago"}</strong>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-success w-100 rounded-3 py-2 fw-semibold mb-2 d-flex align-items-center justify-content-center gap-2"
                  onClick={() => setContactModalOpen(true)}
                >
                  <MessageCircle size={17} />
                  <span>{isAr ? "تواصل مع المستقل (قبل الطلب)" : "Contact Seller (Inquiry)"}</span>
                </button>

                {profileRef ? (
                  <Link
                    to={`/freelancers/profile/${encodeURIComponent(profileRef)}`}
                    state={{ freelancer, service }}
                    className="btn btn-outline-primary w-100 rounded-3 py-2 fw-semibold"
                  >
                    {isAr ? "عرض الملف الشخصي" : "View Profile"}
                  </Link>
                ) : null}
              </div>
            </aside>
          </div>
        </div>

        {/* Lightbox Modal */}
      {lightboxOpen ? (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-90 d-flex align-items-center justify-content-center z-3" onClick={() => setLightboxOpen(false)}>
          <button type="button" className="position-absolute top-0 end-0 m-4 btn btn-light rounded-circle" onClick={() => setLightboxOpen(false)}>
            <X size={24} />
          </button>
          <img src={activeImage} alt={title} className="mw-90 mh-90 object-fit-contain rounded-3" onError={handleImageError} />
        </div>
      ) : null}

      {/* Pre-Order Contact Seller Modal */}
      <ContactSellerModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        freelancer={freelancer}
        service={service}
      />
    </div>
  );
}
